import { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AuthContext } from "@/App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  FileText,
  CheckCircle2,
  Clock,
  Download,
  Pen,
  Building2,
  Users,
  IndianRupee,
  Calendar,
  Shield,
  FileCheck,
  Loader2,
  Briefcase,
  Scale,
  MapPin,
  Package,
  Ban,
  HandshakeIcon,
  AlertTriangle
} from "lucide-react";
import jsPDF from "jspdf";

export default function SaleAgreement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dealIdFromUrl = searchParams.get("deal");

  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(dealIdFromUrl || "");
  const [agreement, setAgreement] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [witnessName, setWitnessName] = useState("");
  const [signing, setSigning] = useState(false);

  // Form state for creating new agreement
  const [form, setForm] = useState({
    purchase_price: "",
    payment_structure: "lump_sum",
    earnout_terms: "",
    closing_date: "",
    transition_period_days: 30,
    non_compete_years: 2,
    non_compete_radius_km: 50,
    included_assets: [],
    excluded_assets: [],
    seller_representations: [],
    buyer_representations: [],
    special_conditions: "",
    escrow_amount: "",
    escrow_release_conditions: ""
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (selectedDealId) {
      fetchAgreement();
    }
  }, [selectedDealId]);

  const fetchData = async () => {
    try {
      const [dealsRes, templatesRes] = await Promise.all([
        api.get("/sale-agreement/deals/eligible"),
        api.get("/sale-agreement/templates")
      ]);
      setDeals(dealsRes.data);
      setTemplates(templatesRes.data);
      
      if (dealsRes.data.length > 0 && !selectedDealId) {
        setSelectedDealId(dealsRes.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreement = async () => {
    try {
      const { data } = await api.get(`/sale-agreement/${selectedDealId}`);
      setAgreement(data);
    } catch (e) {
      setAgreement(null);
    }
  };

  const handleAssetToggle = (asset, type) => {
    const key = type === "included" ? "included_assets" : "excluded_assets";
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(asset) 
        ? prev[key].filter(a => a !== asset)
        : [...prev[key], asset]
    }));
  };

  const handleRepresentationToggle = (rep, role) => {
    const key = role === "seller" ? "seller_representations" : "buyer_representations";
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(rep)
        ? prev[key].filter(r => r !== rep)
        : [...prev[key], rep]
    }));
  };

  const createAgreement = async () => {
    if (!form.purchase_price || !form.closing_date) {
      toast.error("Please fill in required fields (Purchase Price, Closing Date)");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        deal_id: selectedDealId,
        purchase_price: parseFloat(form.purchase_price),
        payment_structure: form.payment_structure,
        earnout_terms: form.earnout_terms || null,
        closing_date: form.closing_date,
        transition_period_days: parseInt(form.transition_period_days),
        non_compete_years: parseInt(form.non_compete_years),
        non_compete_radius_km: parseInt(form.non_compete_radius_km),
        included_assets: form.included_assets,
        excluded_assets: form.excluded_assets,
        seller_representations: form.seller_representations,
        buyer_representations: form.buyer_representations,
        special_conditions: form.special_conditions || null,
        escrow_amount: form.escrow_amount ? parseFloat(form.escrow_amount) : null,
        escrow_release_conditions: form.escrow_release_conditions || null
      };

      const { data } = await api.post("/sale-agreement", payload);
      setAgreement(data);
      toast.success("Sale Agreement created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agreement");
    } finally {
      setCreating(false);
    }
  };

  const signAgreement = async () => {
    if (!signatureName.trim()) {
      toast.error("Please enter your name to sign");
      return;
    }

    setSigning(true);
    try {
      const role = user.id === agreement.buyer.id ? "buyer" : "seller";
      await api.post(`/sale-agreement/${selectedDealId}/sign`, {
        signature_name: signatureName,
        signature_role: role,
        witness_name: witnessName || null
      });
      fetchAgreement();
      setShowSignDialog(false);
      setSignatureName("");
      setWitnessName("");
      toast.success("Agreement signed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign agreement");
    } finally {
      setSigning(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generatePDF = () => {
    if (!agreement) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("DEFINITIVE SALE AGREEMENT", pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Business Purchase and Sale Contract", pageWidth / 2, y, { align: "center" });
    y += 15;

    // Agreement ID
    doc.setFontSize(8);
    doc.text(`Agreement ID: ${agreement.id}`, 20, y);
    y += 10;

    // Parties
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("1. PARTIES", 20, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`SELLER: ${agreement.seller.name} (${agreement.seller.email})`, 25, y);
    y += 6;
    doc.text(`BUYER: ${agreement.buyer.name} (${agreement.buyer.email})`, 25, y);
    y += 12;

    // Business
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("2. SUBJECT BUSINESS", 20, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Business Name: ${agreement.business_name}`, 25, y);
    y += 5;
    doc.text(`Industry: ${agreement.business_industry}`, 25, y);
    y += 5;
    doc.text(`Location: ${agreement.business_location}`, 25, y);
    y += 12;

    // Financial Terms
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("3. PURCHASE PRICE AND PAYMENT", 20, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Purchase Price: ${formatCurrency(agreement.financial_terms.purchase_price)}`, 25, y);
    y += 5;
    doc.text(`Payment Structure: ${agreement.financial_terms.payment_structure.replace("_", " ").toUpperCase()}`, 25, y);
    y += 5;
    if (agreement.financial_terms.escrow_amount) {
      doc.text(`Escrow Amount: ${formatCurrency(agreement.financial_terms.escrow_amount)}`, 25, y);
      y += 5;
    }
    y += 7;

    // Closing Terms
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("4. CLOSING", 20, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Closing Date: ${formatDate(agreement.closing_terms.closing_date)}`, 25, y);
    y += 5;
    doc.text(`Transition Period: ${agreement.closing_terms.transition_period_days} days`, 25, y);
    y += 12;

    // Non-Compete
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("5. NON-COMPETE COVENANT", 20, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Duration: ${agreement.non_compete.years} years`, 25, y);
    y += 5;
    doc.text(`Geographic Radius: ${agreement.non_compete.radius_km} km`, 25, y);
    y += 12;

    // Assets (simplified)
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("6. ASSETS INCLUDED", 20, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const includedCount = agreement.assets.included.length;
    doc.text(`${includedCount} asset categories included (see full agreement for details)`, 25, y);
    y += 12;

    // Check if we need a new page
    if (y > 240) {
      doc.addPage();
      y = 20;
    }

    // Signatures
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("SIGNATURES", 20, y);
    y += 12;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    
    // Seller
    doc.text("SELLER:", 20, y);
    y += 6;
    if (agreement.signatures.seller) {
      doc.text(`Signed: ${agreement.signatures.seller.name}`, 20, y);
      y += 5;
      doc.text(`Date: ${formatDate(agreement.signatures.seller.signed_at)}`, 20, y);
    } else {
      doc.text("_______________________________", 20, y);
      y += 5;
      doc.text("(Pending)", 20, y);
    }
    y += 12;

    // Buyer
    doc.text("BUYER:", 20, y);
    y += 6;
    if (agreement.signatures.buyer) {
      doc.text(`Signed: ${agreement.signatures.buyer.name}`, 20, y);
      y += 5;
      doc.text(`Date: ${formatDate(agreement.signatures.buyer.signed_at)}`, 20, y);
    } else {
      doc.text("_______________________________", 20, y);
      y += 5;
      doc.text("(Pending)", 20, y);
    }

    // Footer
    y = doc.internal.pageSize.getHeight() - 15;
    doc.setFontSize(7);
    doc.text("Generated by ReBusi - India's Premier Business Marketplace", pageWidth / 2, y, { align: "center" });

    doc.save(`Sale_Agreement_${agreement.id.substring(0, 8)}.pdf`);
    toast.success("PDF downloaded!");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 pt-28 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Definitive Sale Agreement</h1>
          <p className="text-slate-600 mb-8">Please login to access sale agreement tools</p>
          <Button onClick={() => navigate("/auth")} className="bg-orange-500 hover:bg-orange-600">
            Sign In to Continue
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const selectedDeal = deals.find(d => d.id === selectedDealId);
  const canSign = agreement && user && agreement.status !== "fully_executed" && (
    (user.id === agreement.buyer.id && !agreement.signatures.buyer) ||
    (user.id === agreement.seller.id && !agreement.signatures.seller)
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <HandshakeIcon className="w-8 h-8 text-orange-500" />
              Definitive Sale Agreement
            </h1>
            <p className="text-slate-600 mt-1">Create and sign the final business purchase contract</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : deals.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Deals Ready for Sale Agreement</h2>
              <p className="text-slate-600 mb-6">
                You need deals at Escrow or Closing stage (Stage 5+) to create a Sale Agreement.
              </p>
              <Button onClick={() => navigate("/deals")} variant="outline">
                View My Deals
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Deal Selector */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Eligible Deals</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[400px]">
                  {deals.map((deal) => (
                    <div
                      key={deal.id}
                      onClick={() => setSelectedDealId(deal.id)}
                      className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedDealId === deal.id ? "bg-orange-50 border-l-4 border-l-orange-500" : ""
                      }`}
                    >
                      <h3 className="font-semibold text-slate-900 text-sm">{deal.listing_name}</h3>
                      <p className="text-xs text-slate-500">{deal.listing_industry}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {deal.current_stage}
                        </Badge>
                        {deal.has_sale_agreement && (
                          <Badge className={`text-xs ${
                            deal.sale_agreement_status === "fully_executed" 
                              ? "bg-emerald-100 text-emerald-700" 
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {deal.sale_agreement_status === "fully_executed" ? "Signed" : "Draft"}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {selectedDeal && (
                <Card className="mb-6">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">{selectedDeal.listing_name}</h2>
                        <p className="text-slate-600">{selectedDeal.listing_industry} • {formatCurrency(selectedDeal.deal_value)}</p>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700">
                        Stage: {selectedDeal.current_stage}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!agreement ? (
                /* Create Agreement Form */
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-orange-500" />
                      Create Sale Agreement
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Financial Terms */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <IndianRupee className="w-4 h-4" />
                        Financial Terms
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Purchase Price (₹) *</Label>
                          <Input
                            type="number"
                            value={form.purchase_price}
                            onChange={(e) => setForm(prev => ({ ...prev, purchase_price: e.target.value }))}
                            placeholder="e.g., 5000000"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Payment Structure</Label>
                          <Select 
                            value={form.payment_structure} 
                            onValueChange={(val) => setForm(prev => ({ ...prev, payment_structure: val }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {templates?.payment_structures.map(ps => (
                                <SelectItem key={ps.id} value={ps.id}>{ps.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {form.payment_structure === "earnout" && (
                          <div className="space-y-2 md:col-span-2">
                            <Label>Earnout Terms</Label>
                            <Textarea
                              value={form.earnout_terms}
                              onChange={(e) => setForm(prev => ({ ...prev, earnout_terms: e.target.value }))}
                              placeholder="Describe the earnout conditions..."
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label>Escrow Amount (₹)</Label>
                          <Input
                            type="number"
                            value={form.escrow_amount}
                            onChange={(e) => setForm(prev => ({ ...prev, escrow_amount: e.target.value }))}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Closing Terms */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Closing Terms
                      </h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Closing Date *</Label>
                          <Input
                            type="date"
                            value={form.closing_date}
                            onChange={(e) => setForm(prev => ({ ...prev, closing_date: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Transition Period (Days)</Label>
                          <Input
                            type="number"
                            value={form.transition_period_days}
                            onChange={(e) => setForm(prev => ({ ...prev, transition_period_days: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Non-Compete */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Ban className="w-4 h-4" />
                        Non-Compete Covenant
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Duration (Years)</Label>
                          <Input
                            type="number"
                            value={form.non_compete_years}
                            onChange={(e) => setForm(prev => ({ ...prev, non_compete_years: e.target.value }))}
                            min={1}
                            max={10}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Geographic Radius (KM)</Label>
                          <Input
                            type="number"
                            value={form.non_compete_radius_km}
                            onChange={(e) => setForm(prev => ({ ...prev, non_compete_radius_km: e.target.value }))}
                            min={10}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Assets */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Assets Included
                      </h3>
                      <Accordion type="multiple" className="w-full">
                        {templates?.asset_categories.map(cat => (
                          <AccordionItem key={cat.id} value={cat.id}>
                            <AccordionTrigger className="text-sm">{cat.name}</AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2">
                                {cat.items.map(item => (
                                  <div key={item} className="flex items-center gap-2">
                                    <Checkbox
                                      id={item}
                                      checked={form.included_assets.includes(item)}
                                      onCheckedChange={() => handleAssetToggle(item, "included")}
                                    />
                                    <label htmlFor={item} className="text-sm text-slate-700">{item}</label>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>

                    {/* Special Conditions */}
                    <div className="space-y-2">
                      <Label>Special Conditions</Label>
                      <Textarea
                        value={form.special_conditions}
                        onChange={(e) => setForm(prev => ({ ...prev, special_conditions: e.target.value }))}
                        placeholder="Any additional terms or conditions..."
                        rows={3}
                      />
                    </div>

                    <Button
                      onClick={createAgreement}
                      disabled={creating}
                      className="bg-orange-500 hover:bg-orange-600 w-full"
                    >
                      {creating ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                      ) : (
                        "Create Sale Agreement"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                /* View Agreement */
                <>
                  {/* Status Card */}
                  <Card className="mb-6">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileCheck className={`w-8 h-8 ${
                            agreement.status === "fully_executed" ? "text-emerald-500" : "text-amber-500"
                          }`} />
                          <div>
                            <h3 className="font-semibold text-slate-900">Agreement Status</h3>
                            <p className="text-sm text-slate-600">
                              {agreement.status === "fully_executed" ? "Fully Executed - Deal Complete!" :
                               agreement.status === "pending_countersign" ? "Awaiting Countersignature" :
                               "Draft - Pending Signatures"}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={generatePDF}>
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                          {canSign && (
                            <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
                              <DialogTrigger asChild>
                                <Button className="bg-orange-500 hover:bg-orange-600">
                                  <Pen className="w-4 h-4 mr-2" />
                                  Sign Agreement
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Sign Sale Agreement</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                    <div className="flex items-start gap-2">
                                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                      <p className="text-sm text-amber-800">
                                        This is a legally binding document. By signing, you agree to all terms including the purchase price of <strong>{formatCurrency(agreement.financial_terms.purchase_price)}</strong> and all associated conditions.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Your Full Legal Name *</Label>
                                    <Input
                                      value={signatureName}
                                      onChange={(e) => setSignatureName(e.target.value)}
                                      placeholder="Enter your full legal name"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Witness Name (Optional)</Label>
                                    <Input
                                      value={witnessName}
                                      onChange={(e) => setWitnessName(e.target.value)}
                                      placeholder="Witness name if applicable"
                                    />
                                  </div>
                                  <Button
                                    onClick={signAgreement}
                                    disabled={signing || !signatureName.trim()}
                                    className="w-full bg-orange-500 hover:bg-orange-600"
                                  >
                                    {signing ? (
                                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing...</>
                                    ) : (
                                      "Confirm & Sign"
                                    )}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Agreement Document */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Definitive Sale Agreement</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">
                        {/* Header */}
                        <div className="text-center border-b border-slate-200 pb-4">
                          <h2 className="text-xl font-bold text-slate-900">DEFINITIVE SALE AGREEMENT</h2>
                          <p className="text-sm text-slate-500">Business Purchase and Sale Contract</p>
                          <p className="text-xs text-slate-400 mt-2">Agreement ID: {agreement.id}</p>
                        </div>

                        {/* Parties */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">1. PARTIES</h3>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500 uppercase">Seller</p>
                              <p className="font-semibold text-slate-900">{agreement.seller.name}</p>
                              <p className="text-sm text-slate-600">{agreement.seller.email}</p>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500 uppercase">Buyer</p>
                              <p className="font-semibold text-slate-900">{agreement.buyer.name}</p>
                              <p className="text-sm text-slate-600">{agreement.buyer.email}</p>
                            </div>
                          </div>
                        </div>

                        {/* Business */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">2. SUBJECT BUSINESS</h3>
                          <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-slate-500">Business Name</p>
                                <p className="font-semibold text-slate-900">{agreement.business_name}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Industry</p>
                                <p className="font-semibold text-slate-900">{agreement.business_industry}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-xs text-slate-500">Location</p>
                                <p className="font-semibold text-slate-900">{agreement.business_location}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Financial Terms */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">3. PURCHASE PRICE AND PAYMENT</h3>
                          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-slate-500">Purchase Price</p>
                                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(agreement.financial_terms.purchase_price)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Payment Structure</p>
                                <p className="font-semibold text-slate-900 capitalize">{agreement.financial_terms.payment_structure.replace("_", " ")}</p>
                              </div>
                              {agreement.financial_terms.escrow_amount && (
                                <div>
                                  <p className="text-xs text-slate-500">Escrow Amount</p>
                                  <p className="font-semibold text-slate-900">{formatCurrency(agreement.financial_terms.escrow_amount)}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Closing */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">4. CLOSING</h3>
                          <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                            <div>
                              <p className="text-xs text-slate-500">Closing Date</p>
                              <p className="font-semibold text-slate-900">{formatDate(agreement.closing_terms.closing_date)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Transition Period</p>
                              <p className="font-semibold text-slate-900">{agreement.closing_terms.transition_period_days} days</p>
                            </div>
                          </div>
                        </div>

                        {/* Non-Compete */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">5. NON-COMPETE COVENANT</h3>
                          <p className="text-sm text-slate-700">
                            The Seller agrees not to engage in any competing business within a radius of <strong>{agreement.non_compete.radius_km} kilometers</strong> for a period of <strong>{agreement.non_compete.years} years</strong> from the closing date.
                          </p>
                        </div>

                        {/* Assets */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">6. ASSETS INCLUDED IN SALE</h3>
                          <div className="grid md:grid-cols-2 gap-2">
                            {agreement.assets.included.slice(0, 10).map((asset, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                {asset}
                              </div>
                            ))}
                            {agreement.assets.included.length > 10 && (
                              <p className="text-sm text-slate-500 md:col-span-2">
                                ...and {agreement.assets.included.length - 10} more items
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Signatures */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">SIGNATURES</h3>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className={`p-4 rounded-lg border-2 ${
                              agreement.signatures.seller ? "border-emerald-200 bg-emerald-50" : "border-dashed border-slate-300"
                            }`}>
                              <p className="text-xs text-slate-500 uppercase">Seller Signature</p>
                              {agreement.signatures.seller ? (
                                <>
                                  <p className="font-semibold text-slate-900 mt-2 text-lg italic">
                                    {agreement.signatures.seller.name}
                                  </p>
                                  <p className="text-xs text-emerald-600 mt-1">
                                    Signed on {formatDate(agreement.signatures.seller.signed_at)}
                                  </p>
                                </>
                              ) : (
                                <p className="text-slate-400 mt-2 italic">Pending signature...</p>
                              )}
                            </div>
                            <div className={`p-4 rounded-lg border-2 ${
                              agreement.signatures.buyer ? "border-emerald-200 bg-emerald-50" : "border-dashed border-slate-300"
                            }`}>
                              <p className="text-xs text-slate-500 uppercase">Buyer Signature</p>
                              {agreement.signatures.buyer ? (
                                <>
                                  <p className="font-semibold text-slate-900 mt-2 text-lg italic">
                                    {agreement.signatures.buyer.name}
                                  </p>
                                  <p className="text-xs text-emerald-600 mt-1">
                                    Signed on {formatDate(agreement.signatures.buyer.signed_at)}
                                  </p>
                                </>
                              ) : (
                                <p className="text-slate-400 mt-2 italic">Pending signature...</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="text-center pt-4 border-t border-slate-200">
                          <p className="text-xs text-slate-400">
                            Generated by ReBusi - India's Premier Business Marketplace
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
